import { type Ref, readonly, ref, watch } from 'vue'

import { closeModal, openModal } from '@ds/components/ModalBase'

import { useShoppingAssistantWebSidebar } from '../stores/useShoppingAssistantWebSidebar'

import type {
  NewChatDialog,
  WebShoppingAssistantHost,
} from './useShoppingAssistantHost.injection-key'
import { useShoppingAssistantViewport } from './useShoppingAssistantViewport'

// Single RevDialog instance shared by the new-chat confirmation.
export const WEB_SIDEBAR_CONFIRM_DIALOG_NAME =
  'shopping-assistant-web-sidebar-confirm'

/**
 * Builds the host adapter for the web sidebar shell (FAB + drawer). Close
 * hides the panel immediately (no confirmation — the conversation stays in
 * server-side history), new-chat is confirmed via a RevDialog (gated on a
 * non-empty conversation), a product card click hides the panel in overlay
 * (small-viewport) mode, and cart/url/ready concerns are no-ops on the web
 * (the front-office cart updates reactively in-app; there is no chat URL in
 * v1).
 *
 * Returns the host plus the bits the container component needs to render the
 * confirmation dialog, to inject a message into a live conversation, and the
 * synced chatId (for pushing browsing context once a chat exists).
 */
export function useWebSidebarShoppingAssistantHost(): {
  host: WebShoppingAssistantHost
  currentDialog: Ref<NewChatDialog | null>
  acceptDialog: () => void
  declineDialog: () => void
  handleDialogClose: (closingEvent?: { cause?: string }) => void
  chatId: Readonly<Ref<string | null>>
} {
  const store = useShoppingAssistantWebSidebar()
  const { isOverlay } = useShoppingAssistantViewport()

  const receivedEvent = ref('')
  const currentDialog = ref<NewChatDialog | null>(null)
  const chatId = ref<string | null>(null)

  // Bridge store.pendingMessage → receivedEvent so the running chat sends
  // the message inline without remounting. Entry points (pills) set
  // pendingMessage when the sidebar is already open and shouldResumeLatest
  // is true.
  watch(
    () => store.pendingMessage,
    (message) => {
      if (message) {
        receivedEvent.value = message
        store.clearPendingMessage()
      }
    },
  )

  // The chat is being replaced (user-initiated new chat) or the panel closed:
  // the old chatId is no longer valid until synced again.
  watch(
    () => store.conversationKey,
    () => {
      chatId.value = null
    },
  )
  watch(
    () => store.isOpen,
    (isOpen) => {
      if (!isOpen) chatId.value = null
    },
  )

  function openConfirmDialog(dialog: NewChatDialog) {
    // Single-slot: drop a new request while one is already pending so the
    // user's in-flight choice (onAccept/onDecline) is never swapped under them.
    if (currentDialog.value) {
      return
    }

    currentDialog.value = dialog
    openModal(WEB_SIDEBAR_CONFIRM_DIALOG_NAME)
  }

  function acceptDialog() {
    const dialog = currentDialog.value
    currentDialog.value = null
    closeModal(WEB_SIDEBAR_CONFIRM_DIALOG_NAME)
    dialog?.onAccept()
  }

  function declineDialog() {
    const dialog = currentDialog.value
    currentDialog.value = null
    closeModal(WEB_SIDEBAR_CONFIRM_DIALOG_NAME)
    dialog?.onDecline?.()
  }

  function handleDialogClose(closingEvent?: { cause?: string }) {
    if (closingEvent?.cause === 'accept') return

    const dialog = currentDialog.value
    if (!dialog) return

    currentDialog.value = null
    dialog.onDecline?.()
  }

  const host: WebShoppingAssistantHost = {
    hostType: 'web-sidebar',
    receivedEvent: readonly(receivedEvent),
    clearReceivedEvent: () => {
      receivedEvent.value = ''
    },
    close() {
      store.close()
    },
    onProductCardClick() {
      if (isOverlay.value) store.close()
    },
    newChat(dialog) {
      // Plus button: confirm only when there's a real conversation to lose.
      if (store.hasUserMessages) {
        openConfirmDialog(dialog)

        return
      }

      dialog.onAccept()
    },
    reportConversation: (value: boolean) => {
      store.setHasUserMessages(value)
    },
    syncChatId: (id: string) => {
      chatId.value = id
    },
  }

  return {
    host,
    currentDialog,
    acceptDialog,
    declineDialog,
    handleDialogClose,
    chatId: readonly(chatId),
  }
}
